import { useQuery, useMutation } from '@apollo/client';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useVisitorId } from '@xing-com/hub';

import { preferencesData } from './data';
import { CreateLoggedOutPreferenceDocument } from './mutations/create-preferences.mutation.gql-types';
import { DeleteLoggedOutPreferenceDocument } from './mutations/delete-preferences.mutation.gql-types';
import { ReadLoggedOutPreferencesDocument } from './mutations/read-preferences.query.gql-types';
import type { Preferences } from './types';
import { convertUrnToPreferences } from './utils';

export function useReadLoggedOutPreferences(): {
  loading: boolean;
  preferences: Preferences | null;
} {
  const [loading, setLoading] = useState(true);
  const [urns, setUrns] = useState<string[]>([]);

  const visitorId = useVisitorId();

  const {
    data,
    loading: fetchingPreferences,
    refetch,
  } = useQuery(ReadLoggedOutPreferencesDocument, {
    variables: { visitorId },
  });

  useEffect(() => {
    if (fetchingPreferences) return;

    const hasNextPage =
      data?.viewer?.welcomeLoggedOutDataPage?.welcomeLoggedOutDataPaginated
        ?.pageInfo.hasNextPage;

    setUrns((old) => [
      ...old,
      ...(data?.viewer?.welcomeLoggedOutDataPage?.welcomeLoggedOutDataPaginated?.edges.map(
        (edge) => edge?.node?.urn
      ) || []),
    ]);

    if (hasNextPage) {
      refetch({
        after:
          data?.viewer?.welcomeLoggedOutDataPage?.welcomeLoggedOutDataPaginated
            ?.pageInfo.endCursor,
        visitorId: visitorId,
      });
    } else {
      setLoading(false);
    }
  }, [data, refetch, visitorId, fetchingPreferences]);

  const result = useMemo(() => {
    if (loading) return { loading, preferences: null };

    return { loading, preferences: convertUrnToPreferences(urns) };
  }, [loading, urns]);

  return result;
}

type CreateLoggedOutPreferencesResult = {
  create: (key: string, value: string, oldValue?: string) => void;
  loading: boolean;
};

export function useCreateLoggedOutPreference(): CreateLoggedOutPreferencesResult {
  const visitorId = useVisitorId();
  const [createLoggedOutPreferences, { loading: loadingCreate }] = useMutation(
    CreateLoggedOutPreferenceDocument
  );
  const [deleteLoggedOutPreferences, { loading: loadingDelete }] = useMutation(
    DeleteLoggedOutPreferenceDocument
  );

  const createFn = useCallback(
    (key: string, value: string, oldValue?: string) => {
      const preferenceData = preferencesData.options.find(
        (preference) => preference.preference === key
      );

      if (!preferenceData) return;

      const newUrn = value
        ? preferenceData.urnPrefix + preferenceData.urns[value]
        : '';
      const oldUrn = oldValue
        ? preferenceData.urnPrefix + preferenceData.urns[oldValue]
        : '';

      if (oldUrn) {
        deleteLoggedOutPreferences({
          variables: { input: { urn: oldUrn, visitorId: visitorId } },
        });
      }

      if (newUrn) {
        createLoggedOutPreferences({
          variables: { input: { urns: [newUrn], visitorId: visitorId } },
        });
      }
    },
    [createLoggedOutPreferences, deleteLoggedOutPreferences, visitorId]
  );

  return { create: createFn, loading: loadingCreate || loadingDelete };
}
